import type { SgTimepickerCustomEvent } from '@sg-bootstrap/components';
import { SgTimepicker } from '@sg-bootstrap/components/dist/react-library/src/components';
import type { TimeEventData } from '@sg-bootstrap/components/dist/types/components';
import { isDefined } from '@sgme/fp';
import { useCallbackRef } from 'hooks/useCallbackRef';
import type { LanguageIsoCode } from 'locales';
import { useCallback } from 'react';
import type { ColorLevel, Media, State } from 'types/sgBootstrap';

type TimeFormat = 12 | 24;
interface TimePickerCommonProps {
  className?: string;
  availableHours?: readonly number[];
  closeWhenClickOutside?: boolean;
  disabled?: boolean;
  disabledDropdown?: boolean;
  disabledHours?: readonly number[];
  disabledInput?: boolean;
  format?: TimeFormat;

  hasNow?: boolean;
  height?: `${number}px`;

  labelledBy?: string;
  language?: Extract<LanguageIsoCode, 'en' | 'fr'>;

  minuteStep?: 1 | 5 | 10 | 15;

  noClearButton?: boolean;
  openWhenFocusInput?: boolean;

  preventAutomaticDirection?: boolean;

  readonly?: boolean;

  selectColor?: ColorLevel;

  size?: Media;

  state?: State;

  top?: boolean;

  validationMessage?: string;
}
type TimePickerProps = TimePickerCommonProps &
  (
    | { type: 'string'; value?: string; onValueChanged?: (time?: string) => void }
    | { type: 'object'; value?: TimeEventData; onValueChanged?: (time?: TimeEventData) => void }
  );

export function TimePicker({
  className,
  availableHours,
  disabledHours,
  format = 24,
  type,
  onValueChanged,
  value,
  ...props
}: TimePickerProps) {
  const timeValue = type === 'object' ? value?.time : value;
  const changeValue = useCallback(
    (e?: TimeEventData) => {
      if (!isDefined(e)) {
        onValueChanged?.();
        return;
      }
      if (type === 'object') {
        onValueChanged?.(e);
      } else {
        onValueChanged?.(e.time);
      }
    },
    [type, onValueChanged],
  );
  const handleInputCleared = useCallback(() => {
    changeValue();
  }, [changeValue]);
  const handleTimeSelected = useCallback(
    (event: SgTimepickerCustomEvent<TimeEventData>) => {
      changeValue(event.detail);
    },
    [changeValue],
  );
  const updateRef = useCallback(
    (picker: HTMLSgTimepickerElement) => {
      picker.addEventListener('inputCleared', handleInputCleared);
      picker.addEventListener('timeSelected', handleTimeSelected as EventListener);
    },
    [handleInputCleared, handleTimeSelected],
  );
  const cleanRef = useCallback(
    (picker: HTMLSgTimepickerElement) => {
      picker.removeEventListener('inputCleared', handleInputCleared);
      picker.removeEventListener('timeSelected', handleTimeSelected as EventListener);
    },
    [handleInputCleared, handleTimeSelected],
  );
  const { callback } = useCallbackRef<HTMLSgTimepickerElement>(updateRef, cleanRef);

  return (
    <SgTimepicker
      format={format}
      ref={callback}
      value={timeValue}
      classes={className}
      availableHours={availableHours ? JSON.stringify(availableHours) : availableHours}
      disabledHours={disabledHours ? JSON.stringify(disabledHours) : disabledHours}
      {...props}
    />
  );
}
